import apiService from './api';
import type { ApiResponse } from '../types';
import type {
  OwedToUs, UpstreamAgreement, UpstreamAgreementInput, UpstreamOpenAccruals, UpstreamStatement, UpstreamStatementSummary,
} from '../types/upstream';

/** /api/upstream: what the organisation owes upstream, and what it is owed as a payee. */
const upstreamService = {
  listAgreements(): Promise<ApiResponse<UpstreamAgreement[]>> {
    return apiService.get('/upstream/agreements');
  },
  getAgreement(id: number): Promise<ApiResponse<UpstreamAgreement>> {
    return apiService.get(`/upstream/agreements/${id}`);
  },
  createAgreement(input: UpstreamAgreementInput): Promise<ApiResponse<UpstreamAgreement>> {
    return apiService.post('/upstream/agreements', input);
  },
  updateAgreement(id: number, patch: Partial<Omit<UpstreamAgreementInput, 'starts_on' | 'per_tree_sold' | 'per_tree_per_year'>> & { active?: boolean }):
    Promise<ApiResponse<UpstreamAgreement>> {
    return apiService.put(`/upstream/agreements/${id}`, patch);
  },
  addRate(id: number, rate: { effective_from: string; per_tree_sold: number; per_tree_per_year: number }): Promise<ApiResponse<UpstreamAgreement>> {
    return apiService.post(`/upstream/agreements/${id}/rates`, rate);
  },
  openAccruals(): Promise<ApiResponse<UpstreamOpenAccruals>> {
    return apiService.get('/upstream/accruals/open');
  },
  listStatements(agreementId?: number): Promise<ApiResponse<UpstreamStatementSummary[]>> {
    return apiService.get(`/upstream/statements${agreementId ? `?agreement_id=${agreementId}` : ''}`);
  },
  getStatement(id: number): Promise<ApiResponse<UpstreamStatement>> {
    return apiService.get(`/upstream/statements/${id}`);
  },
  makeStatements(month: string): Promise<ApiResponse<{ period: { from: string; to: string }; made: unknown[] }>> {
    return apiService.post('/upstream/statements/make', { month });
  },
  markPaid(id: number, paid_at: string | null, paid_reference?: string | null): Promise<ApiResponse<UpstreamStatement>> {
    return apiService.put(`/upstream/statements/${id}/paid`, { paid_at, paid_reference: paid_reference || null });
  },
  statementCsv(id: number): Promise<Blob> {
    return apiService.get(`/upstream/statements/${id}/csv`, { responseType: 'blob' }) as unknown as Promise<Blob>;
  },
  owedToUs(): Promise<ApiResponse<OwedToUs>> {
    return apiService.get('/upstream/owed-to-us');
  },
};

export default upstreamService;
